import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2, Mail, Calendar, HardDrive } from "lucide-react";

export default function OAuthCallbackPage() {
  const [, setLocation] = useLocation();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("");
  const [serviceName, setServiceName] = useState<"Gmail" | "Google Calendar" | "Google Drive">("Gmail");
  const [countdown, setCountdown] = useState(5);
  const [isPublicFlow, setIsPublicFlow] = useState(false);
  // Task #128 — use the session-bound workspace slug (from /api/auth/my-workspace)
  // rather than workspaces[0].slug from /api/workspaces, which could route the
  // redirect to a different tenant workspace if the user belongs to multiple workspaces.
  const [sessionWorkspaceSlug, setSessionWorkspaceSlug] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/auth/my-workspace", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => setSessionWorkspaceSlug(data.slug || null))
      .catch(() => setSessionWorkspaceSlug(null));
  }, []);

  const settingsPath = sessionWorkspaceSlug
    ? `/w/${sessionWorkspaceSlug}/settings`
    : "/";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gmail = params.get("gmail");
    const calendar = params.get("calendar");
    const userCalendar = params.get("user_calendar");
    const drive = params.get("drive");
    const reason = params.get("reason");
    const talentCalendar = params.get("talent_calendar");

    if (drive === "connected") {
      setServiceName("Google Drive");
      setStatus("success");
      setMessage("Google Drive has been connected successfully!");
    } else if (drive === "error") {
      setServiceName("Google Drive");
      setStatus("error");
      setMessage(reason ? `Connection failed: ${reason}` : "Failed to connect Google Drive. Please try again.");
    } else if (talentCalendar === "connected") {
      setServiceName("Google Calendar");
      setStatus("success");
      setMessage("Your calendar has been connected successfully! You can close this page.");
      setIsPublicFlow(true);
    } else if (talentCalendar === "error") {
      setServiceName("Google Calendar");
      setStatus("error");
      setMessage(reason ? `Connection failed: ${reason}` : "Failed to connect calendar. Please contact the administrator for a new invitation.");
      setIsPublicFlow(true);
    } else if (gmail === "connected") {
      setServiceName("Gmail");
      setStatus("success");
      setMessage("Your Gmail account has been connected successfully!");
    } else if (gmail === "error") {
      setServiceName("Gmail");
      setStatus("error");
      setMessage(reason ? `Connection failed: ${reason}` : "Failed to connect Gmail. Please contact your administrator.");
    } else if (calendar === "connected" || userCalendar === "success") {
      setServiceName("Google Calendar");
      setStatus("success");
      setMessage("Your Google Calendar has been connected successfully!");
    } else if (calendar === "error" || userCalendar === "error") {
      setServiceName("Google Calendar");
      setStatus("error");
      setMessage(reason ? `Connection failed: ${reason}` : "Failed to connect Google Calendar. Please contact your administrator.");
    } else {
      setStatus("error");
      setMessage("Unknown callback state. Please try again.");
    }
  }, []);

  useEffect(() => {
    if (isPublicFlow) return;

    if (status !== "loading" && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      setLocation(settingsPath);
    }
  }, [status, countdown, setLocation, isPublicFlow, settingsPath]);

  const handleGoToSettings = () => {
    setLocation(settingsPath);
  };

  const ServiceIcon = serviceName === "Gmail" ? Mail : serviceName === "Google Drive" ? HardDrive : Calendar;

  if (isPublicFlow) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center bg-primary/10">
              {status === "loading" && <Loader2 className="w-8 h-8 text-primary animate-spin" />}
              {status === "success" && <CheckCircle className="w-8 h-8 text-green-500" />}
              {status === "error" && <XCircle className="w-8 h-8 text-destructive" />}
            </div>
            <CardTitle className="text-xl">
              {status === "loading" && "Processing..."}
              {status === "success" && "Calendar Connected!"}
              {status === "error" && "Connection Failed"}
            </CardTitle>
            <CardDescription className="mt-2">
              {message}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>Google Calendar Integration</span>
            </div>

            {status === "success" && (
              <p className="text-center text-sm text-muted-foreground">
                Your calendar is now synced. Bookings will automatically appear on your calendar.
              </p>
            )}

            {sessionWorkspaceSlug && (
              <Button
                onClick={handleGoToSettings}
                className="w-full"
                data-testid="button-go-to-workspace"
              >
                Go back to workspace
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center bg-primary/10">
            {status === "loading" && <Loader2 className="w-8 h-8 text-primary animate-spin" />}
            {status === "success" && <CheckCircle className="w-8 h-8 text-green-500" />}
            {status === "error" && <XCircle className="w-8 h-8 text-destructive" />}
          </div>
          <CardTitle className="text-xl">
            {status === "loading" && "Processing..."}
            {status === "success" && `${serviceName} Connected!`}
            {status === "error" && "Connection Failed"}
          </CardTitle>
          <CardDescription className="mt-2">
            {message}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <ServiceIcon className="w-4 h-4" />
            <span>{serviceName} Integration</span>
          </div>

          <p className="text-center text-sm text-muted-foreground">
            Redirecting to settings in {countdown} seconds...
          </p>

          <Button
            onClick={handleGoToSettings}
            className="w-full"
            data-testid="button-go-to-settings"
          >
            Go to Settings Now
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
